import { OfflinePill } from "@rosc/ui/v32/offline-pill";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOnlineStatus } from "@/components/ui/offline-recording/useLocalCapture";
import { useRecorderOfflineMessageStore } from "@/store/recorderOfflineMessage.store";

export const BACK_ONLINE_MS = 2500;

// App-wide connection pill (v3.2 shell, @rosc/ui OfflinePill): while the
// browser is offline, a small pill at the top centre ("You're offline", and
// on wider screens that recordings stay on this device). Back online, a
// short "Back online" that fades. Display only: offline recording keeps its
// own logic.
export const OfflineIndicator = ({
  online: forced,
}: {
  // Stories and tests.
  online?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const live = useOnlineStatus();
  const online = forced ?? live;
  const [backOnline, setBackOnline] = useState(false);
  // A recorder on screen gives its own message (one message, not two).
  const recorderSays = useRecorderOfflineMessageStore(
    (state) => state.owners > 0,
  );
  const wasOffline = useRef(!online);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setBackOnline(false);
      return;
    }
    if (!wasOffline.current) return;
    wasOffline.current = false;
    setBackOnline(true);
    const timer = window.setTimeout(() => setBackOnline(false), BACK_ONLINE_MS);
    return () => window.clearTimeout(timer);
  }, [online]);

  if (recorderSays || (online && !backOnline)) return null;
  return (
    <OfflinePill
      hint={t("offlineIndicator.hint")}
      label={
        online
          ? t("offlineIndicator.backOnline")
          : t("offlineIndicator.offline")
      }
      online={online}
    />
  );
};
